// lib/sentry-base.ts
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { SENTRY_DSN } from "@/lib/constants";
import type { DataCollection, Integration } from "@sentry/core";
import Logger from '@/lib/logger';

const logger = new Logger('pbs:sentry:base:config');
logger.info("Initializing Sentry base config ...");

export interface SentryConfig {
  dsn: string;
  environment: string;
  release?: string;
  sampleRate: number;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  enableOpenTelemetrySetup: boolean;
  dataCollection: DataCollection;
  debug: boolean;
  integrations: Integration[];
}

// Everything else in SentryConfig is decided by the environment switch in
// createSentryConfig: each branch sets `environment` and the sampling rates itself, and
// `release` is named only for production. Accepting a wider type here would advertise
// overrides that are discarded -- and, in the branches that leave `release` alone, would let
// a caller name a release outside production, which is the thing this module exists to prevent.
export type SentryConfigOverrides = Partial<Pick<SentryConfig, 'integrations'>>;

// The header-name fragments v10 scrubbed from headers and query params when sendDefaultPii was
// unset, which it always was here. v11 always masks values whose keys match its own, separate
// list of sensitive fragments (auth, token, session, sso, and others), but it applies this list
// only when configured to.
const PII_KEY_DENYLIST = ["forwarded", "-ip", "remote-", "via", "-user"];

// Sentry v11 replaced sendDefaultPii with dataCollection and inverted the default: left unset,
// it sends cookies, every request and response body, the user and their IP, and queue message
// data. v11 masks the value of the pbs_session_id cookie because its name contains "session",
// but it would send pbs_uid. Queues are turned off as a precaution: background jobs carry
// signed-in email addresses (lib/activate/passport.ts). This pins the user-data categories to what
// v10 collected, so the upgrade sends no more about users than before. Stack frame variables and
// frame context lines are left at the v11 defaults because v10 collected them as well.
export const dataCollection: DataCollection = {
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: PII_KEY_DENYLIST },
    response: { deny: PII_KEY_DENYLIST },
  },
  httpBodies: [],
  urlQueryParams: { deny: PII_KEY_DENYLIST },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  graphQL: { document: false, variables: false },
  queues: false,
};

export const defaultInit: Omit<SentryConfig, 'environment'> = {
  dsn: SENTRY_DSN,
  sampleRate: 0.0,
  tracesSampleRate: 0,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 0.0,
  // ADOT registers the OpenTelemetry tracer provider on the server (see instrumentation.ts), so
  // Sentry must not register its own. @sentry/nextjs defaults this to true. v10 expressed this as
  // skipOpenTelemetrySetup: true, which also turned off Sentry's own HTTP and fetch spans; this
  // option does not, so sentry.server.config.ts turns off server tracing separately.
  enableOpenTelemetrySetup: false,
  dataCollection,
  debug: false,
  integrations: [],
};

// A single container image is promoted from preprod through staging to production,
// so the environment cannot be baked in at build time and has to be resolved per
// runtime. On the server that is a plain environment variable. In the browser it is
// the data attribute the root layouts emit onto <html>, which is available before any
// bundle executes -- process.env is not, because Next.js only inlines NEXT_PUBLIC_*.
export function resolveEnvironment(): string | undefined {
  return typeof window === 'undefined'
    ? process.env.SENTRY_ENVIRONMENT
    : document.documentElement.dataset.sentryEnvironment;
}

export function createSentryConfig(overrides: SentryConfigOverrides = {}): SentryConfig {
  const config: SentryConfig = {
    ...defaultInit,
    environment: "development",
    ...overrides
  };

  const environment = resolveEnvironment();

  switch (environment) {
    case "edcar":
      logger.debug("Overriding Sentry base config with edcar settings...");
      return {
        ...config,
        environment: "edcar",
        sampleRate: 0.5,
        tracesSampleRate: 0.5,
        replaysSessionSampleRate: 0.5,
        replaysOnErrorSampleRate: 1.0,
      };
    case "staging":
      logger.debug("Overriding Sentry base config with staging settings...");
      return {
        ...config,
        environment: "staging",
        sampleRate: 1.0,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
      };
    case "preprod":
      logger.debug("Overriding Sentry base config with preprod settings...");
      return {
        ...config,
        environment: "preprod",
        sampleRate: 1.0,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
      };
    case "production":
      logger.debug("Overriding Sentry base config with production settings...");
      return {
        ...config,
        environment: "production",
        // Only production names a release, so no other environment's events can cause
        // Sentry to auto-create one. The value is baked in by the image build.
        release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
        sampleRate: 0.25,
        // At 1, production exceeded the project's span quota so consistently that in the 30 days to
        // 2026-10-08 Sentry stored browser spans on only one day.
        tracesSampleRate: 0.002,
        // At 0.2, session replays used up the replay quota within minutes of the 2026-09-21 reset,
        // leaving none for replays of sessions that hit an error.
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 1.0,
      };
    default:
      // An unresolved environment means two very different things. Locally there is no
      // SENTRY_ENVIRONMENT and no data attribute, and staying quiet is correct. In a built image
      // NODE_ENV is "production", so an unresolved environment is a misconfiguration: an
      // environment name with no case above, or a document that never emitted the attribute --
      // global-error.tsx renders its own <html> and cannot emit it, because a client component
      // only sees NEXT_PUBLIC_* variables. Reporting those as "unknown" at full error rate is
      // what keeps the event from being dropped by a sampleRate of 0, which in Sentry is
      // indistinguishable from no error having happened. Traces and *session* replays stay off,
      // since their volume is unbounded by definition. Replay on error stays on at the same rate
      // as every other environment: these are the events with the least context of any the app
      // reports -- the environment could not even be determined -- so the replay is what makes
      // them actionable, and it is bounded by the error count.
      if (process.env.NODE_ENV === "production") {
        logger.error(
          `Could not resolve the Sentry environment (got ${JSON.stringify(environment)}); reporting as "unknown".`
        );
        return {
          ...config,
          environment: "unknown",
          sampleRate: 1.0,
          tracesSampleRate: 0,
          replaysSessionSampleRate: 0.0,
          replaysOnErrorSampleRate: 1.0,
        };
      }

      logger.debug("Accepting Sentry default base config without modification...");
      return {
        ...config,
        environment: "development",
      };
  }
}
